"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useRef } from "react";
import type { Swiper as SwiperType } from "swiper";
import { A11y, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/autoplay";

import { resolvePageImage, type SitePageImageMap } from "@/lib/site-page-images";

/** Set to `true` to restore the continuous linear autoplay crawl + hover pause/resume behaviour. */
const CAROUSEL_AUTOPLAY_ENABLED = true;

const DEFAULT_ACCENT = "#f26522";

export type CarouselSlide = {
  title: string;
  body: string;
  footer: { text: string; href?: string; openInNewTab?: boolean };
  image: string;
  alt: string;
};

const DEFAULT_SLIDES: CarouselSlide[] = [
  {
    title: "Entertaining with Law",
    body: "Hi Ho created a social campaign for a law firm, turning legal moments into stories that cut through clutter, humanised law, and captured attention nationwide.",
    footer: { text: "> View Case Study", href: "/case-studies/entertaining-with-law-yes-really" },
    image: "/images/carousel/c_0.jpg",
    alt: "Portrait in a creative workspace",
  },
  {
    title: "Christmas Intrigue",
    body: "Hi Ho created an international\nChristmas email campaign for a\nNelson-based ecommerce brand,\ndriving engagement, building\nexcitement, and online sales.",
    footer: { text: "NZ Nature" },
    image: "/images/carousel/c_1.jpg",
    alt: "Festive flat lay with gifts and decorations",
  },
  {
    title: "The Scent of Candles",
    body: "Hi Ho reignited Living Light by wooing\nloyal fans and younger audiences,\npairing candle scents with food\nvisuals to stop scrollers and pass the\nmatches to a new generation.",
    footer: { text: "> View Case Study", href: "/case-studies/more-than-a-candle-flicker-living-light-s-big-glow-up" },
    image: "/images/carousel/c_2.jpg",
    alt: "Candle and soft lifestyle scene",
  },
  {
    title: "IT for Dummies",
    body: "Hi Ho created a social campaign for an IT company, using humour and relatable moments to stop scrollers in their tracks and make technology feel human and approachable.",
    footer: { text: "Commarc" },
    image: "/images/carousel/c_3.jpg",
    alt: "Bold portrait with colourful styling",
  },
  {
    title: "Pet Food From The Sea",
    body: "Launching a new product line inside an established company like Sealord requires more than just a simple website. It requires a digital platform that can evolve alongside the product, the market, and the brand itself.",
    footer: {
      text: "> View Case Study",
      href: "/case-studies/building-a-launch-platform-for-sealord-skipper-pet-food",
    },
    image: "/images/carousel/c_0.jpg",
    alt: "Placeholder image for Skipper case study",
  },
  {
    title: "Coffee Culture",
    body: "Specialty New Zealand coffee company, L'affare wanted to grow their online coffee sales. Right from the outset, they knew that they need much more than just a website. They needed the whole package, website design and a fully automated digital sales funnel. In stepped Hi Ho.",
    footer: {
      text: "> View Case Study",
      href: "/case-studies/l-affare-needed-the-works-a-new-website-design-and-a-funnel",
    },
    image: "/images/carousel/c_3.jpg",
    alt: "Placeholder image for L'affare case study",
  },
  {
    title: "Matthews Sports Vision",
    body: "Let's be honest, most people wear sunglasses to look good first, eye protection second. Hi Ho built a performance-driven ecommerce platform for Matthews Sports Vision when the light gets harsh and glare won't quit.",
    footer: {
      text: "> View Case Study",
      href: "/case-studies/building-a-performance-driven-ecommerce-platform-for-matthews-sports-vision",
    },
    image: "/images/carousel/c_0.jpg",
    alt: "Athlete in performance sunglasses outdoors",
  },
  {
    title: "The World's Gone To The (Meerkat) Cats!",
    body: "In a world where everyone's doom-scrolling and saying it's all gone to the dogs, Nelson flipped the script and handed the spotlight to its real stars, the meerkats (and Natureland's wild crew), through digital marketing that actually gets seen.",
    footer: {
      text: "> View Case Study",
      href: "/case-studies/the-world-s-gone-to-the-meerkat-cats",
    },
    image: "/images/carousel/c_1.jpg",
    alt: "Meerkat standing on a mound in natural habitat",
  },
  {
    title: "Empowering People Through Artificial Intelligence",
    body: "Hey there, tech devotees, community builders, and curious minds alike, we've got inspiring news from the Nelson AI Sandbox: a website journey that puts community and artificial intelligence front and centre.",
    footer: {
      text: "> View Case Study",
      href: "/case-studies/unveiling-the-future-the-nelson-ai-sandbox-website-journey",
    },
    image: "/images/carousel/c_2.jpg",
    alt: "Person wearing a futuristic headset with digital interface",
  },
  {
    title: "Recyclable packaging made in NZ.",
    body: "Showcasing thermoformed packaging isn't inherently exciting, but it still needs to tell a story while remaining practical and easy to navigate. That was the brief from Custom-Pak, and Hi Ho delivered a structured, intuitive website with just enough flair.",
    footer: {
      text: "> View website",
      href: "https://custompak.co.nz/",
      openInNewTab: true,
    },
    image: "/images/carousel/c_3.jpg",
    alt: "Recyclable thermoformed packaging on a neutral background",
  },
  {
    title: "Bringing Walls to Life",
    body: "Showcasing authentic handmade wall panels relies on imagery that inspires aspiration while capturing real texture and detail. That's exactly what Hi Ho achieved in designing and developing the new Muros website, an experience that feels almost tactile.",
    footer: { text: "> View Case Study", href: "/case-studies/muros" },
    image: "/images/carousel/c_0.jpg",
    alt: "Textured decorative wall panels in a styled interior",
  },
  {
    title: "Hawker House & Bar",
    body: "An Asian street food and hawker market in the heart of Nelson? They needed a website that told its story simply while making reservations effortless. Hi Ho delivered a clean, intuitive experience that captures the atmosphere—you can almost taste the food.",
    footer: {
      text: "> View website",
      href: "https://www.hawkerhouse.co.nz/",
      openInNewTab: true,
    },
    image: "/images/carousel/c_1.jpg",
    alt: "Asian hawker-style street food spread on a table",
  },
];

export type CarouselSectionProps = {
  slides?: CarouselSlide[];
  /** Shared keys use `carousel-0` … `carousel-11`; page-specific override keys use `{imageKeyPrefix}-carousel-{i}`. */
  pageImageMap?: SitePageImageMap;
  /** Optional prefix for page-level overrides. */
  imageKeyPrefix?: string;
  ariaLabel?: string;
  accentColor?: string;
  sectionClassName?: string;
};

function withCmsCarouselImages(
  slides: CarouselSlide[],
  pageImageMap?: SitePageImageMap,
  imageKeyPrefix?: string,
): CarouselSlide[] {
  if (!pageImageMap) return slides;
  return slides.map((slide, i) => {
    const keyWithPrefix = imageKeyPrefix ? `${imageKeyPrefix}-carousel-${i}` : undefined;
    const key = keyWithPrefix && pageImageMap[keyWithPrefix] ? keyWithPrefix : `carousel-${i}`;
    const { src, alt } = resolvePageImage(key, pageImageMap);
    return { ...slide, image: src, alt };
  });
}

/** Swiper `loop` + `slidesPerView: "auto"` needs enough slides or loop is disabled / misbehaves (console warning). Tripling matches the prior working setup. */
function getLoopSlides(slides: CarouselSlide[]) {
  return [...slides, ...slides, ...slides];
}

/** Desktop linear crawl: ms per slide advance (Swiper `speed` at `md+`). Lower = faster. */
const DESKTOP_AUTOPLAY_TRANSITION_MS = 3200;

/** Clicks should snap faster than the autoplay linear crawl. */
const MANUAL_NAV_MS = 480;

/** After unhover, ease to the nearest snap (linear via `.case-study-carousel`) before restarting autoplay — avoids a hard jump from a fractional translate. */
const RESUME_ALIGN_MS = 1200;

/**
 * Freeze the crawl immediately, then fully stop autoplay.
 * - stop/start (not pause/resume): resume() no-ops when `running` is false; pause() can pile transitionend listeners.
 * - After interrupting a long transition, `animating` can stay true; with `loopPreventsSliding` (default true),
 *   slideNext/slidePrev return false until the old transition would have finished — so we clear `animating`.
 */
function freezeCarouselTrack(swiper: SwiperType) {
  if (swiper.destroyed) return;
  const t = swiper.getTranslate();
  swiper.translateTo(t, 0, false, false);
  swiper.animating = false;
  swiper.autoplay?.stop();
}

const navButtonClass =
  "swiper-no-swiping absolute top-1/2 z-20 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-neutral-200/80 bg-white/95 text-neutral-800 shadow-md transition-[opacity,box-shadow] duration-200 hover:bg-white hover:shadow-lg max-md:pointer-events-auto max-md:opacity-100 md:pointer-events-none md:opacity-0 md:group-hover:pointer-events-auto md:group-hover:opacity-100 md:focus-visible:pointer-events-auto md:focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f26522]";

export default function CarouselSection({
  slides = DEFAULT_SLIDES,
  pageImageMap,
  imageKeyPrefix,
  ariaLabel = "Featured work",
  accentColor = DEFAULT_ACCENT,
  sectionClassName = "relative overflow-hidden bg-[#ddd5cc] py-14 font-sans md:py-36",
}: CarouselSectionProps) {
  const resolvedSlides = useMemo(
    () => withCmsCarouselImages(slides, pageImageMap, imageKeyPrefix),
    [slides, pageImageMap, imageKeyPrefix],
  );
  const loopSlides = getLoopSlides(resolvedSlides);
  const swiperRef = useRef<SwiperType | null>(null);
  const hoverEpochRef = useRef(0);
  const resumeAlignFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resumeTransitionEndRef = useRef<((_swiper: SwiperType) => void) | null>(null);

  return (
    <section className={sectionClassName} aria-label={ariaLabel}>
      <div className="site-content pt-4 md:pt-8">
        <div
          className="group relative min-w-0 w-full max-w-full overflow-x-clip"
          onMouseEnter={() => {
            if (!CAROUSEL_AUTOPLAY_ENABLED) return;
            hoverEpochRef.current += 1;
            if (resumeAlignFallbackRef.current) {
              clearTimeout(resumeAlignFallbackRef.current);
              resumeAlignFallbackRef.current = null;
            }
            const s0 = swiperRef.current;
            if (s0 && resumeTransitionEndRef.current) {
              s0.off("transitionEnd", resumeTransitionEndRef.current);
              resumeTransitionEndRef.current = null;
            }
            const s = swiperRef.current;
            if (!s) return;
            freezeCarouselTrack(s);
          }}
          onMouseLeave={() => {
            if (!CAROUSEL_AUTOPLAY_ENABLED) return;
            const instance = swiperRef.current;
            if (!instance || instance.destroyed) return;
            const leaveEpoch = hoverEpochRef.current;
            const ap = instance.autoplay;
            if (!ap) return;

            if (resumeAlignFallbackRef.current) {
              clearTimeout(resumeAlignFallbackRef.current);
              resumeAlignFallbackRef.current = null;
            }
            if (resumeTransitionEndRef.current) {
              instance.off("transitionEnd", resumeTransitionEndRef.current);
              resumeTransitionEndRef.current = null;
            }

            ap.paused = false;
            instance.animating = false;
            instance.slideToClosest(RESUME_ALIGN_MS, true);

            let alignFinished = false;
            const finishAlignAndStartCrawl = () => {
              if (alignFinished) return;
              alignFinished = true;
              instance.off("transitionEnd", onAlignTransitionEnd);
              if (resumeAlignFallbackRef.current) {
                clearTimeout(resumeAlignFallbackRef.current);
                resumeAlignFallbackRef.current = null;
              }
              resumeTransitionEndRef.current = null;
              if (instance.destroyed || leaveEpoch !== hoverEpochRef.current) return;
              ap.start();
            };
            const onAlignTransitionEnd = () => {
              finishAlignAndStartCrawl();
            };

            resumeTransitionEndRef.current = onAlignTransitionEnd;
            instance.on("transitionEnd", onAlignTransitionEnd);
            resumeAlignFallbackRef.current = setTimeout(
              finishAlignAndStartCrawl,
              RESUME_ALIGN_MS + 300,
            );
          }}
        >
          <Swiper
            modules={CAROUSEL_AUTOPLAY_ENABLED ? [A11y, Autoplay] : [A11y]}
            slidesPerView="auto"
            spaceBetween={24}
            speed={480}
            loop
            loopPreventsSliding={false}
            loopAdditionalSlides={2}
            watchSlidesProgress
            watchOverflow={false}
            /** Finger swipe on small screens; disabled from `md` up so mouse drag does not fight linear autoplay. */
            allowTouchMove
            preventClicks={false}
            preventClicksPropagation={false}
            touchStartPreventDefault={false}
            threshold={10}
            noSwiping
            noSwipingClass="swiper-no-swiping"
            /** Mobile-first: no crawl; `768` breakpoint enables linear autoplay on desktop only. */
            autoplay={false}
            a11y={{
              enabled: true,
              prevSlideMessage: "Previous slide",
              nextSlideMessage: "Next slide",
            }}
            breakpoints={{
              640: { spaceBetween: 24 },
              768: {
                allowTouchMove: false,
                ...(CAROUSEL_AUTOPLAY_ENABLED
                  ? {
                      speed: DESKTOP_AUTOPLAY_TRANSITION_MS,
                      autoplay: {
                        delay: 0,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: false,
                      },
                    }
                  : {}),
              },
              1024: { spaceBetween: 28 },
            }}
            className="case-study-carousel relative mx-0! w-full max-w-full min-w-0 overflow-hidden pb-2 select-none [&_*]:select-none"
            onSwiper={(instance) => {
              swiperRef.current = instance;
              if (!CAROUSEL_AUTOPLAY_ENABLED) return;
              // Breakpoint-only autoplay does not always start on first paint; start here so the crawl
              // runs on load without requiring a hover + mouseleave cycle.
              const startDesktopCrawl = () => {
                if (instance.destroyed) return;
                if (typeof window === "undefined" || window.innerWidth < 768) return;
                instance.autoplay?.start();
              };
              requestAnimationFrame(() => {
                requestAnimationFrame(startDesktopCrawl);
              });
            }}
            onBreakpoint={(swiper) => {
              if (!CAROUSEL_AUTOPLAY_ENABLED || swiper.destroyed) return;
              if (typeof window !== "undefined" && window.innerWidth >= 768) {
                swiper.autoplay?.start();
              }
            }}
          >
            <button
              type="button"
              slot="container-start"
              aria-label="Previous slide"
              className={`${navButtonClass} left-2 md:left-4`}
              onClick={() => {
                const s = swiperRef.current;
                if (!s || s.destroyed) return;
                s.slidePrev(MANUAL_NAV_MS);
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
                className="mr-0.5"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              slot="container-start"
              aria-label="Next slide"
              className={`${navButtonClass} right-2 md:right-4`}
              onClick={() => {
                const s = swiperRef.current;
                if (!s || s.destroyed) return;
                s.slideNext(MANUAL_NAV_MS);
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
                className="ml-0.5"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {loopSlides.map((slide, index) => (
              <SwiperSlide
                key={`${slide.title}-${index}`}
                className="h-auto! w-72! shrink-0 md:w-80! lg:w-96!"
              >
                <article className="flex h-full flex-col gap-3">
                  <div className="relative aspect-4/5 w-full overflow-hidden rounded-2xl bg-neutral-300 shadow-sm md:rounded-3xl">
                    <Image
                      src={slide.image}
                      alt={slide.alt}
                      fill
                      draggable={false}
                      priority={index === 0}
                      className="object-cover"
                      sizes="(max-width: 640px) 288px, (max-width: 1024px) 320px, 384px"
                    />
                  </div>
                  <div className="flex w-full min-h-[400px] flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-sm md:rounded-3xl md:p-6">
                    <span
                      className="mb-2 text-3xl leading-none"
                      style={{ color: accentColor }}
                      aria-hidden
                    >
                      *
                    </span>
                    <h3
                      className="text-lg font-semibold md:text-2xl"
                      style={{ color: accentColor }}
                    >
                      {slide.title}
                    </h3>
                    <p className="mt-2 min-h-0 flex-1 text-xs leading-normal text-neutral-700 line-clamp-5 md:text-lg md:line-clamp-6">
                      {slide.body}
                    </p>
                    {slide.footer.href?.trim() ? (
                      slide.footer.openInNewTab ? (
                        <a
                          href={slide.footer.href.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="swiper-no-swiping relative z-10 mt-3 shrink-0 text-lg font-medium text-neutral-500 underline-offset-2 transition-colors hover:text-neutral-900 hover:underline focus-visible:rounded-sm focus-visible:text-neutral-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f26522]"
                        >
                          {slide.footer.text}
                        </a>
                      ) : (
                        <Link
                          href={slide.footer.href.trim()}
                          prefetch
                          className="swiper-no-swiping relative z-10 mt-3 shrink-0 text-lg font-medium text-neutral-500 underline-offset-2 transition-colors hover:text-neutral-900 hover:underline focus-visible:rounded-sm focus-visible:text-neutral-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f26522]"
                        >
                          {slide.footer.text}
                        </Link>
                      )
                    ) : (
                      <span className="mt-3 shrink-0 text-lg font-medium text-neutral-500">
                        {slide.footer.text}
                      </span>
                    )}
                  </div>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
